import React, { useMemo } from 'react';
import TransformSection from './common/transform-section';
import { registerInspector } from './inspector-registry';
import ColorField from './shared/color-field';
import CornerRadiusSection from './shared/corner-radius-section';
import FillGradientSection from './shared/fill-gradient-section';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import SlotFilterSection from './shared/slot-filter-section';
import NumberField from './shared/number-field';
import { rectCornerStrategy } from './shared/corner-radii-strategies';
import { useCommonFabricProp } from './shared/use-common-fabric-prop';
import { useCornerRadii } from './shared/use-corner-radii';
import { useFillGradient } from './shared/use-fill-gradient';
import type { ObjectInspectorProps } from './types';

const MIXED = '(разное)';

const RectInspector: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const fill = useCommonFabricProp<string>(objects, 'fill', editor);
  const stroke = useCommonFabricProp<string>(objects, 'stroke', editor);
  const strokeWidth = useCommonFabricProp<number>(objects, 'strokeWidth', editor);
  const opacity = useCommonFabricProp<number>(objects, 'opacity', editor);
  const cornerStrategy = useMemo(() => rectCornerStrategy(editor), [editor]);
  const radii = useCornerRadii(objects, editor, cornerStrategy);
  const gradient = useFillGradient(objects, editor);
  // When the gradient is on, `fill` is a gradient (not a hex string), so the plain
  // fill picker is meaningless — the colors are edited in the Gradient section.
  const gradientActive = gradient.enabled.value === true || gradient.enabled.isMixed;

  return (
    <>
      <TransformSection objects={objects} editor={editor} />
      <InspectorSection title="Внешний вид">
        {!gradientActive && (
          <InspectorRow label="Заливка">
            <ColorField
              value={fill.isMixed ? undefined : fill.value}
              onChange={fill.setValue}
              placeholder={fill.isMixed ? MIXED : undefined}
            />
          </InspectorRow>
        )}
        <InspectorRow label="Обводка">
          <ColorField
            value={stroke.isMixed ? undefined : stroke.value}
            onChange={stroke.setValue}
            placeholder={stroke.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Толщина">
          <NumberField
            value={strokeWidth.isMixed ? undefined : strokeWidth.value}
            onChange={strokeWidth.setValue}
            min={0}
            placeholder={strokeWidth.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Прозрачность">
          <NumberField
            value={opacity.isMixed ? undefined : opacity.value}
            onChange={opacity.setValue}
            step={0.05}
            min={0}
            max={1}
            placeholder={opacity.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <CornerRadiusSection control={radii} />
      <FillGradientSection control={gradient} />
      <SlotFilterSection objects={objects} editor={editor} />
    </>
  );
};

export default RectInspector;

registerInspector('rect', RectInspector);
